import './styles.sass';

import React from 'react';
import PropTypes from 'prop-types';

import { Tooltip as ReactTooltip } from 'react-tooltip';

import DropdownMenu from 'components/DropdownMenu';
import useExplorerData, { Loading } from 'hooks/useExplorerData';
import useQueryParams from 'hooks/useQueryParams';

// Private component that encapsulates a row in the dropdown
const Row = ({ labelText, href, onClick, supportUrl, tooltipText }) => {
  if (href === null) {
    return null;
  }

  return (
    <div className="row">
      <a
        className="info"
        href={supportUrl}
        target="_blank"
        rel="noreferrer"
        aria-label={tooltipText}
        data-tooltip-id="Export-tooltip"
        data-tooltip-content={tooltipText}
        data-event="focus mouseenter"
        data-event-off="blur mouseleave"
      >
        &#xe911;
      </a>
      <a onClick={onClick} className="action" target="_blank" rel="noreferrer" href={href}>
        {labelText}
      </a>
      <ReactTooltip id="Export-tooltip" effect="solid" place="left" />
    </div>
  );
};

Row.propTypes = {
  labelText: PropTypes.string.isRequired,
  href: PropTypes.string,
  onClick: PropTypes.func.isRequired,
  supportUrl: PropTypes.string,
  tooltipText: PropTypes.string
};

const Export = ({ endpoint, resultsCount, help }) => {
  const params = useQueryParams();

  const { data, loading } = useExplorerData(`exports/${endpoint}`, params, {
    reloadOn: ['filters', 'sortOrder'],
    enabled: resultsCount !== 0
  });

  const trackCSVEvent = () => Analytics.trackEvent('export-csv', { endpoint });
  const trackCSVUTF8Event = () => Analytics.trackEvent('export-csv-utf8', { endpoint });
  const trackAPIEvent = () => Analytics.trackEvent('export-api', { endpoint });

  const isLoading = loading === Loading.NOT_STARTED || loading === Loading.IN_PROGRESS;
  const csvUrl = data?.csv ?? null;
  const csvUtf8Url = data?.csv_utf8 ?? null;
  const apiUrl = data?.api ?? null;

  if (resultsCount === 0 || isLoading || (!csvUrl && !csvUtf8Url && !apiUrl)) {
    return <div className="Export" data-testid="export" />;
  }

  return (
    <div className="Export" data-testid="export">
      <div id="CSV-Export-Dropdown">
        <DropdownMenu title={I18n.t('Explorer.Export.header')}>
          <div className="CSV-Export-Dropdown-Items-Wrapper"></div>
          <div className="CSV-Export-Dropdown-Items">
            <Row
              labelText={I18n.t('Explorer.Export.download_csv.link')}
              href={csvUrl}
              onClick={trackCSVEvent}
              supportUrl={help.csv}
              tooltipText={I18n.t('Explorer.Export.download_csv.tip')}
            />
            <Row
              labelText={I18n.t('Explorer.Export.download_csv_utf8.link')}
              href={csvUtf8Url}
              onClick={trackCSVUTF8Event}
              supportUrl={help.csv}
              tooltipText={I18n.t('Explorer.Export.download_csv_utf8.tip')}
            />
            <Row
              labelText={I18n.t('Explorer.Export.download_json.link')}
              href={apiUrl}
              onClick={trackAPIEvent}
              supportUrl={help.api}
              tooltipText={I18n.t('Explorer.Export.download_json.tip')}
            />
          </div>
        </DropdownMenu>
      </div>
    </div>
  );
};

Export.propTypes = {
  endpoint: PropTypes.string,
  resultsCount: PropTypes.number,
  help: PropTypes.object
};

export default Export;
